import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import UserContext from '../../../../components/User_Context.js';
import * as menu from "../../../../assets/menus/menu.js";
import * as breadcrumb from "../../../../components/ui/breadcrumb.js";
import * as api from "../../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import { formBuilderLabels } from '../../../_label/form_builder/form_builder_labels.js';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import Form_Compiler from '../compilerNew.js';
import {calculateHeaderHeight} from '../../../../components/ui_utils.js';

const NewForm_Dashboard = () => {
    const { currentUser } = useContext(UserContext);
    const [assignments, setAssignments] = useState(null);
    const [activeTab, setActiveTab] = useState('Active'); // 'Active', 'Completed' or 'Approved'
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { t } = useTranslation();
    const MySwal = withReactContent(Swal);
    const [headerHeight, setHeaderHeight] = useState(0);
    
    // Form viewing state
    const [viewingForm, setViewingForm] = useState(null);
    const [viewingFormData, setViewingFormData] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(true);

    // Initialize translations and setup
    useEffect(() => {
        setHeaderHeight(calculateHeaderHeight());
        
        const i18n = menu.getI18n();
        i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
        i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);

        menu.write({ user: currentUser() });

        // Set breadcrumb
        breadcrumb.set([
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/dashboard", name: t('assignmentsDashboard', { ns: 'labels' }) }
        ]);

        loadDashboardData();
    }, []);

    // Load dashboard data using the new endpoint
    const loadDashboardData = async (status = null) => {
        try {
            setLoading(true);
            setError(null);

            const body = {};
            if (status) {
                body.status = status;
            }

            const result = await api.sendMessage("darm/formAssignments/getDashboardData", {
                user: currentUser(),
                body: body
            });

            if (result && result.success) {
                setAssignments(result.assignments || []);
            } else {
                throw new Error(result?.error || 'Failed to load dashboard data');
            }
        } catch (error) {
            console.error("Error loading dashboard data:", error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Filter assignments based on active tab
    const getFilteredAssignments = () => {
        if (!assignments) return [];
        return assignments.filter(assignment => assignment.Status === activeTab);
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format date and time for display
    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Get status badge color
    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'Saved': return 'bg-warning text-dark';
            case 'Compiled': return 'bg-info text-white';
            case 'Approved': return 'bg-success text-white';
            default: return 'bg-secondary text-white';
        }
    };

    // Navigate to form compilation
    const handleCompileForm = (assignment) => {
        // Check if there are remaining cycles
        if (assignment.remainingCycles <= 0) {
            MySwal.fire({
                title: t('attention', { ns: 'labels' }),
                text: t('allCyclesCompleted', { ns: 'labels' }),
                icon: 'warning'
            });
            return;
        }

        // Navigate to form compilation
        window.location.href = `/form/compile/${assignment.FormId}`;
    };

    // Handle viewing a completed form
    const handleViewCompletion = async (assignment, completion) => {
        // For active assignments, redirect to the compilation page
        if (activeTab === 'Active') {
            window.location.href = `/form/compile/${assignment.FormId}/${completion.Id}`;
            return;
        }

        // For non-active assignments, show in read-only mode (existing behavior)
        try {
            // Show loading indicator
            MySwal.fire({
                title: t('loading', { ns: 'labels' }),
                text: t('loadingFormData', { ns: 'labels', defaultValue: 'Loading form data...' }),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Load both the form completion data AND the form structure
            const [completionResult, formResult] = await Promise.all([
                // Load the form completion data
                api.sendMessage("darm/formCompletion/get", {
                    user: currentUser(),
                    body: {
                        formId: assignment.FormId,
                        completionId: completion.Id
                    }
                }),
                // Load the form structure
                api.sendMessage("darm/formBuilder/get", {
                    user: currentUser(),
                    body: {
                        id: assignment.FormId
                    }
                })
            ]);

            MySwal.close();

            if (completionResult?.success && completionResult?.formCompletion && formResult?.success && formResult?.form) {
                // Combine the form structure with the completion data
                const combinedData = {
                    ...completionResult.formCompletion,
                    form: formResult.form
                };
                
                console.log("🔍 [DEBUG] Combined form data:", combinedData);
                
                // Set read-only mode (always true for non-active assignments)
                setIsReadOnly(true);
                
                // Set the viewing form data
                setViewingForm(assignment);
                setViewingFormData(combinedData);
                
                // Update breadcrumb when viewing a form
                updateBreadcrumbForFormView(assignment.FormName || 'Form');
            } else {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: completionResult?.message || formResult?.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                    icon: 'error'
                });
            }
        } catch (error) {
            MySwal.close();
            console.error("Error loading form for viewing:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('failedToLoadForm', { ns: 'labels', defaultValue: 'Failed to load form data' }),
                icon: 'error'
            });
        }
    };

    // Handle closing the form viewer
    const handleCloseFormViewer = () => {
        setViewingForm(null);
        setViewingFormData(null);
        setIsReadOnly(true); // Reset to default read-only state
        // Reset breadcrumb to dashboard
        setBreadCrumb();
    };

    // Update breadcrumb when viewing a form
    const updateBreadcrumbForFormView = (formName) => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/dashboard", name: t('assignmentsDashboard', { ns: 'labels' }) },
            { link: "#", name: `${t('viewingForm', { ns: 'labels', defaultValue: 'Viewing Form' })}: ${formName}` }
        ];
        breadcrumb.set(path);
    };

    // Set breadcrumb for the dashboard
    const setBreadCrumb = () => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/dashboard", name: t('assignmentsDashboard', { ns: 'labels' }) }
        ];
        breadcrumb.set(path);
    };



    // Assignment Card Component
    const AssignmentCard = ({ assignment }) => {
        const [expanded, setExpanded] = useState(false);

        return (
                          <div className="card mb-4 shadow-sm h-auto" style={{ border: '1px solid #e9ecef', borderRadius: '12px' }}>
                {/* Card Header */}
                <div className="card-header border-0 position-relative" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px 12px 0 0' }}>
                    <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-2">
                            <h4 className="card-title mb-0 fw-bold text-dark me-3">
                                {assignment.FormName}
                            </h4>
                            {assignment.FormCode && (
                                <span className="badge bg-light text-dark border">{assignment.FormCode}</span>
                            )}
                            {assignment.Status === 'Approved' && (
                                <span className="badge ms-2 bg-success">
                                    <i className="fas fa-thumbs-up me-1"></i>
                                    {t('Approved', { ns: 'labels' })}
                                </span>
                            )}
                        </div>
                        
                        <div className="d-flex flex-wrap gap-2 mb-2">
                            {assignment.FormGroup && (
                                <span className="badge bg-primary">{assignment.FormGroup}</span>
                            )}
                            {assignment.GroupName && (
                                <span className="badge bg-info">
                                    <i className="fas fa-users me-1"></i>
                                    {assignment.GroupName}
                                </span>
                            )}
                        </div>

                        <div className="d-flex flex-wrap gap-3">
                            <small className="text-muted">
                                <i className="fas fa-calendar me-1"></i>
                                {t('createdDate', { ns: 'labels' })}: {formatDate(assignment.ItemCreated)}
                            </small>
                            
                            {(assignment.StartDate || assignment.EndDate) && (
                                <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    {t('assignmentPeriod', { ns: 'labels' })}: 
                                    {assignment.StartDate && ` ${formatDate(assignment.StartDate)}`}
                                    {assignment.StartDate && assignment.EndDate && ' - '}
                                    {assignment.EndDate && ` ${formatDate(assignment.EndDate)}`}
                                </small>
                            )}
                            
                            {assignment.ToValidate && (
                                <small className="text-warning">
                                    <i className="fas fa-shield-alt me-1"></i>
                                    {t('requiresValidation', { ns: 'labels' })}
                                </small>
                            )}
                        </div>
                    </div>
                    
                    {/* Pulsanti posizionati in assoluto nell'angolo in basso a destra */}
                    <div className="position-absolute" style={{ bottom: '1rem', right: '1rem' }}>
                        <div className="d-flex gap-1">
                            {assignment.Status === 'Active' && assignment.remainingCycles > 0 && (
                                <a
                                    className="text-primary fs-13"
                                    title={t('compileForm', { ns: 'labels' })}
                                    role="button"
                                    onClick={() => handleCompileForm(assignment)}
                                    style={{ 
                                        display: 'inline-block',
                                        padding: '2px 4px',
                                        minWidth: '30px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <i className="fa-solid fa-pen-to-square fa-lg"></i>
                                </a>
                            )}
                            
                            <a
                                className="text-secondary fs-13"
                                title={expanded ? t('hide', { ns: 'labels' }) + ' ' + t('details', { ns: 'labels' }) : t('show', { ns: 'labels' }) + ' ' + t('details', { ns: 'labels' })}
                                role="button"
                                onClick={() => setExpanded(!expanded)}
                                style={{ 
                                    display: 'inline-block',
                                    padding: '2px 4px',
                                    minWidth: '30px',
                                    cursor: 'pointer'
                                }}
                            >
                                <i className={`fas fa-chevron-${expanded ? 'up' : 'down'} fa-lg`}></i>
                            </a>
                        </div>
                    </div>
                </div>

                {/* Card Body - Details */}
                <div className="card-body">
                    {/* Assignment Info Grid */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <div className="text-center p-4 bg-white border rounded-3 shadow-sm">
                                <div className="fs-2 fw-bold text-success mb-1">{assignment.completedCycles}</div>
                                <div className="text-muted small fw-medium">{t('completedCycles', { ns: 'labels' })}</div>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="text-center p-4 bg-white border rounded-3 shadow-sm">
                                <div className="fs-2 fw-bold text-primary mb-1">{assignment.remainingCycles}</div>
                                <div className="text-muted small fw-medium">{t('remainingCycles', { ns: 'labels' })}</div>
                            </div>
                        </div>
                                                 <div className="col-md-4">
                             <div className="text-center p-4 border rounded-3 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                                 <div className="fs-2 fw-bold text-secondary mb-1">{assignment.totalCycles}</div>
                                 <div className="text-muted small fw-medium">{t('totalCycles', { ns: 'labels' })}</div>
                             </div>
                         </div>
                    </div>



                    {/* Completions List */}
                    {expanded && (
                        <div className="mt-4">
                                                         <div className="d-flex align-items-center justify-content-between mb-3">
                                 <h6 className="mb-0 text-secondary">
                                     <i className="fas fa-list me-2"></i>
                                     {t('formCompletions', { ns: 'labels' })}
                                 </h6>
                                 <span className="text-muted fw-medium">({assignment.completions?.length || 0})</span>
                             </div>
                            
                            {assignment.completions && assignment.completions.length > 0 ? (
                                <div className="list-group list-group-flush">
                                    {assignment.completions.map((completion, index) => (
                                        <div 
                                            key={completion.Id} 
                                            className="list-group-item list-group-item-action border-0 rounded-3 mb-2 shadow-sm"
                                            style={{ 
                                                backgroundColor: completion.countsAsCycle ? '#f8fff8' : '#fffaf0',
                                                cursor: 'pointer',
                                                border: completion.countsAsCycle ? '1px solid #d4edda' : '1px solid #ffeaa7'
                                            }}
                                            onClick={() => handleViewCompletion(assignment, completion)}
                                        >
                                            <div className="d-flex justify-content-between align-items-center py-2">
                                                <div className="d-flex align-items-center">
                                                    <span className="badge me-3" style={{ 
                                                        backgroundColor: completion.countsAsCycle ? '#28a745' : '#6c757d', 
                                                        color: 'white',
                                                        minWidth: '35px'
                                                    }}>
                                                        #{index + 1}
                                                    </span>
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <span className={`badge ${getStatusBadgeColor(completion.Status)}`}>
                                                                {t(completion.Status, { ns: 'labels' })}
                                                            </span>
                                                            {completion.countsAsCycle && (
                                                                <span className="badge bg-success">
                                                                    <i className="fas fa-star me-1"></i>
                                                                    {t('completedCycle', { ns: 'labels' })}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <small className="text-muted">
                                                            <i className="fas fa-user me-1"></i>
                                                            {completion.Compiler}
                                                        </small>
                                                    </div>
                                                </div>

                                                <div className="text-end">
                                                    {completion.ToValidate && completion.Validated && (
                                                        <div className="text-success small mb-1">
                                                            <i className="fas fa-check-circle me-1"></i>
                                                            {t('validated', { ns: 'labels' })}
                                                            {completion.ValidatedBy && (
                                                                <div className="text-muted">{completion.ValidatedBy}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    <small className="text-muted">
                                                        <i className="fas fa-clock me-1"></i>
                                                        {formatDateTime(completion.ItemCreated)}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center text-muted p-5 bg-light rounded-3">
                                    <i className="fas fa-inbox fa-3x mb-3 opacity-50"></i>
                                    <div className="fw-medium">{t('noCompletionsYet', { ns: 'labels' })}</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // If viewing a form, show the Form_Compiler in read-only mode
    if (viewingForm && viewingFormData) {
        return (
            <div className="container-fluid">
                <Form_Compiler
                    form={viewingFormData.form}
                    formCompletion={viewingFormData}
                    language="it"
                    readOnly={isReadOnly}
                    onClose={handleCloseFormViewer}
                    colors={{
                        primary: '#f05f23',
                        primaryHover: '#d44d12',
                        primaryLight: '#fef0ea',
                        secondary: '#6c757d',
                        secondaryLight: '#e9ecef',
                        dark: '#343a40',
                        light: '#f8f9fa',
                        white: '#ffffff',
                        success: '#28a745',
                        danger: '#dc3545',
                        warning: '#ffc107',
                        info: '#17a2b8'
                    }}
                />
            </div>
        );
    }

    return (
        <div className="container-fluid" style={{ height: `calc(100vh - ${headerHeight}px)` }}>
            {/* Fixed Header */}
            <header className="bg-white shadow-sm" style={{ position: 'fixed', top: `${headerHeight}px`, left: '240px', right: 0, zIndex: 1000 }}>
                <div className="container-fluid d-flex justify-content-between align-items-center py-2">
                    {/* Breadcrumb */}
                    <ol className="breadcrumb mb-0"></ol>
                    
                    {/* Action buttons */}
                    <div className="d-flex gap-3">
                        <a 
                            className="text-primary fs-13" 
                            href="/form_builder/finder" 
                            role="button" 
                            title={t('searchForms', { ns: 'labels' })}
                        >
                            <i className="fa-solid fa-magnifying-glass fa-2x"></i>
                        </a>
                        <a 
                            className="text-primary fs-13" 
                            onClick={() => loadDashboardData(activeTab)}
                            role="button" 
                            title={t('refresh', { ns: 'labels' })}
                        >
                            <i className="fa-solid fa-sync fa-2x"></i>
                        </a>
                    </div>
                </div>
            </header>

            {/* Scrollable Content */}
            <main className="overflow-auto" style={{ paddingTop: '60px', height: '100%', backgroundColor: '#f4f6f9' }}>
                <div className="container-fluid py-3">
                    <div className="row">
                        <div className="col-12">
                                                         <div className="card h-auto">
                                {/* Dashboard Header */}
                                <div className="card-header bg-white border-0 pb-3">
                                    <div>
                                        <h3 className="fw-bold mb-2 text-dark">{t('assignmentsDashboard', { ns: 'labels' })}</h3>
                                        <p className="mb-0 text-muted">
                                            {t('assignmentsDashboardDescription', { ns: 'labels' })}
                                        </p>
                                    </div>
                                </div>

                                <div className="card-body p-0">
                                    {/* Modern Tabs */}
                                    <div className="d-flex border-bottom bg-light">
                                        <button 
                                            className={`btn btn-light flex-grow-1 text-center border-0 rounded-0 ${activeTab === 'Active' ? 'border-bottom border-primary border-3' : ''}`}
                                            onClick={() => handleTabChange('Active')}
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                <i className="fas fa-clock me-2"></i>
                                                <span>{t('activeAssignments', { ns: 'labels' })}</span>
                                                {assignments && activeTab === 'Active' && (
                                                    <span className="badge ms-2 bg-primary">
                                                        {assignments.filter(a => a.Status === 'Active').length}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                        <button 
                                            className={`btn btn-light flex-grow-1 text-center border-0 rounded-0 ${activeTab === 'Completed' ? 'border-bottom border-warning border-3' : ''}`}
                                            onClick={() => handleTabChange('Completed')}
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                <i className="fas fa-check-circle me-2"></i>
                                                <span>{t('completedAssignments', { ns: 'labels' })}</span>
                                                {assignments && activeTab === 'Completed' && (
                                                    <span className="badge ms-2 bg-warning text-dark">
                                                        {assignments.filter(a => a.Status === 'Completed').length}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                        <button 
                                            className={`btn btn-light flex-grow-1 text-center border-0 rounded-0 ${activeTab === 'Approved' ? 'border-bottom border-success border-3' : ''}`}
                                            onClick={() => handleTabChange('Approved')}
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                <i className="fas fa-thumbs-up me-2"></i>
                                                <span>{t('approvedAssignments', { ns: 'labels', defaultValue: 'Assegnazioni Approvate' })}</span>
                                                {assignments && activeTab === 'Approved' && (
                                                    <span className="badge ms-2 bg-success">
                                                        {assignments.filter(a => a.Status === 'Approved').length}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    </div>

                                    {/* Tab Content */}
                                    <div className="p-4">
                                        {loading && (
                                            <div className="d-flex justify-content-center py-5">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                                                </div>
                                            </div>
                                        )}

                                        {error && (
                                            <div className="alert alert-danger">
                                                <strong>{t('error', { ns: 'labels' })}:</strong> {error}
                                            </div>
                                        )}

                                        {!loading && !error && (
                                            <>
                                                {getFilteredAssignments().length > 0 ? (
                                                    <div className="row">
                                                        <div className="col-12">
                                                            {getFilteredAssignments().map((assignment) => (
                                                                <AssignmentCard 
                                                                    key={assignment.Id} 
                                                                    assignment={assignment} 
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-5">
                                                        <i className={`fas ${activeTab === 'Active' ? 'fa-clock' : activeTab === 'Completed' ? 'fa-check-circle' : 'fa-thumbs-up'} fa-3x text-muted mb-3`}></i>
                                                        <h5 className="text-muted">
                                                            {activeTab === 'Active' 
                                                                ? t('noActiveAssignments', { ns: 'labels' })
                                                                : activeTab === 'Completed'
                                                                ? t('noCompletedAssignments', { ns: 'labels' })
                                                                : t('noApprovedAssignments', { ns: 'labels', defaultValue: 'Nessuna assegnazione approvata' })
                                                            }
                                                        </h5>
                                                        <p className="text-muted">
                                                            {activeTab === 'Active' 
                                                                ? t('noActiveAssignmentsDescription', { ns: 'labels' })
                                                                : activeTab === 'Completed'
                                                                ? t('noCompletedAssignmentsDescription', { ns: 'labels' })
                                                                : t('noApprovedAssignmentsDescription', { ns: 'labels', defaultValue: 'Non ci sono assegnazioni approvate da visualizzare.' })
                                                            }
                                                        </p>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default NewForm_Dashboard;
